import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, Observable, throwError } from 'rxjs';
import { PublicApi } from '../constants';
import { AppResponse } from '../constants/ApiResponse';
import {
  Auth,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
} from '@angular/fire/auth';
import { SecureStorageService } from '@services/secure-storage.service';
import { AuthInterceptor } from '../constants/interceptor';
import { MicroApiService, MS_TOKEN_KEY } from '../constants/micro-api.service';

type PublicApiKeys = keyof typeof PublicApi | 'BILLING_DISCOUNT';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // Authentication state only. The navigation draft never contains credentials or tokens.
  private onboardingSession: { email: string; token: string } | null = null;

  bindOnboardingSession(email: string, token: string | undefined): boolean {
    if (!token || localStorage.getItem(MS_TOKEN_KEY) !== token) return false;
    this.onboardingSession = { email: email.trim().toLowerCase(), token };
    return true;
  }

  hasOnboardingSession(email: string): boolean {
    const session = this.onboardingSession;
    if (!session || session.email !== email.trim().toLowerCase() ||
        localStorage.getItem(MS_TOKEN_KEY) !== session.token) return false;
    if (session.token.split('.').length === 3) {
      try {
        const encoded = session.token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(encoded));
        if (typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now()) return false;
      } catch { return false; }
    }
    // Opaque gateway tokens have no local expiry claim; the API remains authoritative.
    return true;
  }

  clearOnboardingSession(): void {
    this.onboardingSession = null;
  }

  constructor(
    private router: Router,
    private auth: Auth,
    private secureStorage: SecureStorageService,
    private micro: MicroApiService,
  ) { }

  login1(username: string, password: string): boolean {
    if (username === 'admin' && password === '123') {
      const token = 'fake-jwt-token'; // Normally you would get this from the server
      void this.secureStorage.setItem('authToken', token);
      void this.secureStorage.setItem('userRole', 'admin'); // Store user role for authorization
      return true;
    } else return false;
  }

  isAuthenticated(): boolean {
    const token = this.secureStorage.getItem('authToken');
    const userDetails = this.getUserDetails();
    if (!token || !userDetails || !userDetails?.verified || !userDetails?.isCompany || userDetails?.isCompany?.length === 0  ) {
      return false;
    }
    return true;
  }

  // Check if user is logged in
  isLoggedIn(): boolean {
    return !!this.secureStorage.getItem('authToken');
  }

  logout1(): void {
    this.secureStorage.removeItem('authToken');
    this.secureStorage.removeItem('userRole');
    this.router.navigate(['auth/login']);
  }

  // Get user role for authorization
  getUserRole(): string | null {
    return this.secureStorage.getItem('userRole');
  }

  getUserEmail(): string | null {
    const userDetails = this.getUserDetails();
    if (userDetails) {
      return userDetails?.email || null;
    }
    return null;
  }
  getUserId(): string | null {
    const userDetails = this.getUserDetails();
    if (userDetails) {
      return userDetails?.id || null;
    }
    return null;
  }

  // Every call goes to the microservice gateway (MicroApiService.route); there is no other
  // backend. The second argument says whether a key with no gateway action answers empty (a read)
  // or fails (a write) — either way, without leaving the browser.
  getAll(endpoint: PublicApiKeys): Observable<AppResponse> {
    return this.micro.route(endpoint, true).pipe(catchError(this.handleError));
  }
  delete(endpoint: PublicApiKeys): Observable<AppResponse> {
    return this.micro.route(endpoint, false).pipe(catchError(this.handleError));
  }
  deletebyId(
    endpoint: PublicApiKeys,
    id: string | number
  ): Observable<AppResponse> {
    // The id travels as a FIELD in the action payload, not as a path segment.
    return this.micro.route(endpoint, false, undefined, id).pipe(catchError(this.handleError));
  }
  register(endpoint: PublicApiKeys, model: any): Observable<AppResponse> {
    return this.micro.route(endpoint, false, model).pipe(catchError(this.handleError));
  }
  login(endpoint: PublicApiKeys, model: any): Observable<AppResponse> {
    return this.micro.route(endpoint, false, model).pipe(catchError(this.handleError));
  }
  verify(endpoint: PublicApiKeys, model: any): Observable<AppResponse> {
    return this.micro.route(endpoint, false, model).pipe(catchError(this.handleError));
  }
  logout(endpoint: PublicApiKeys, model: any): Observable<AppResponse> {
    // The gateway has no client logout: its token simply expires. Sent as a read so a key
    // with no action answers {} and the caller goes on to clear the local session.
    return this.micro.route(endpoint, true, model).pipe(catchError(this.handleError));
  }

  getById(endpoint: PublicApiKeys, id: any): Observable<AppResponse> {
    return this.micro.route(endpoint, true, { id }, id).pipe(catchError(this.handleError));
  }

  updateLanguage(language: string): Observable<AppResponse> {
    return this.micro.route('UPDATE_LANGUAGE', false, { language }).pipe(catchError(this.handleError));
  }

 private handleError(error: any): Observable<never> {
  if (error.error instanceof ErrorEvent) {
    // Client-side error
    console.error(`Client-side error: ${error.error.message}`);
  } else {
    // Server-side error
    console.error(`Server-side error: ${error.status} - ${error.message}`);
  }
  
  // ✅ DON'T override the actual error - pass it through
  return throwError(() => error);
}

  setToken(data: any) {
    AuthInterceptor.resetSessionExpired();
    void this.secureStorage.setItem('authToken', data.user.authToken);
    void this.secureStorage.setItem('jwtToken', data.user.authToken);
    sessionStorage.setItem('jwtToken', data.user.authToken);
    // localStorage.setItem('userRole', data.user._roles.name);
    this.setUserDetails(data);
  }

  setTokenII(data: any) {
    AuthInterceptor.resetSessionExpired();
    void this.secureStorage.setItem('authToken', data.authToken);
    void this.secureStorage.setItem('jwtToken', data.authToken);
    sessionStorage.setItem('jwtToken', data.authToken);
    // localStorage.setItem('userRole', data.user._roles.name);
    this.setUserDetails(data.data);
  }


  setUserToken(data: any) {
    AuthInterceptor.resetSessionExpired();
    void this.secureStorage.setItem('userToken', data.user.authToken);
    void this.secureStorage.setItem('jwtToken', data.user.authToken);
    // localStorage.setItem('userRole', data.user._roles.name);
    sessionStorage.setItem('jwtToken', data.user.authToken);
  }

  /**
   * A half-finished signup. LOGIN refuses it with NOT_VERIFIED / NO_COMPANY and, for the
   * account owner, hands back the onboarding token. Keeps that token and answers the step to
   * resume at — the business profile when there is no company yet, otherwise the email
   * verification. Null for any other refusal.
   */
  resumeOnboarding(err: any): string | null {
    const code = err?.error?.code;
    const user = err?.error?.data?.user;
    if ((code !== 'NOT_VERIFIED' && code !== 'NO_COMPANY') || !user?.authToken) return null;
    localStorage.setItem(MS_TOKEN_KEY, user.authToken);
    this.setUserToken({ user });
    return user.isCompany ? '/auth/send-otp' : '/auth/company';
  }

  /** Only accept a signup refusal's onboarding token when it belongs to the submitted email. */
  acceptOnboardingUser(user: any, email: string): boolean {
    if (!user?.authToken || !user?.email ||
        user.email.trim().toLowerCase() !== email.trim().toLowerCase()) return false;
    localStorage.setItem(MS_TOKEN_KEY, user.authToken);
    this.setUserToken({ user });
    return true;
  }

  setUserDetails(data: any) {
    void this.secureStorage.setItem('userDetails', data.user);
  }

  getUserDetails(): any {
    return this.secureStorage.getItem('userDetails');
  }

  signInWithGoogle() {
    return signInWithPopup(this.auth, new GoogleAuthProvider());
  }

  signInWithFacebook() {
    return signInWithPopup(this.auth, new FacebookAuthProvider());
  }
}
